'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

// ─── WhatsApp ─────────────────────────────────────────────────────────────────

export async function sendWhatsApp(type: string, to: string, data: Record<string, any>) {
  const supabase = createClient();
  try {
    if (!to) {
      console.warn('WhatsApp skipped: no contact number provided');
      return;
    }
    const { error } = await supabase.functions.invoke('send-whatsapp', {
      body: { type, to, data },
    });
    if (error) console.error('WhatsApp send error:', error.message);
  } catch (err: any) {
    console.error('WhatsApp function error:', err.message);
  }
}

// ─── User Profile ─────────────────────────────────────────────────────────────

export async function getUserProfile(userId: string) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    return data;
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return null;
  }
}

export async function getAllClients() {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('role', 'client')
      .order('created_at', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return data || [];
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return [];
  }
}

export async function deleteClient(clientId: string): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();
  try {
    // Delete projects first (cascade may handle this, but explicit is safer)
    await supabase.from('projects').delete().eq('client_id', clientId);
    // Delete user profile
    const { error: profileError } = await supabase.from('user_profiles').delete().eq('id', clientId);
    if (profileError) return { success: false, error: profileError.message };
    // Delete auth user via admin API
    const res = await fetch('/api/admin/delete-client', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clientId }),
    });
    const data = await res.json();
    if (!res.ok) return { success: false, error: data.error || 'Failed to delete auth user' };
    return { success: true };
  } catch (err: any) {
    return { success: false, error: err.message };
  }
}

// ─── Projects ─────────────────────────────────────────────────────────────────

export async function getClientProject(clientId: string) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('client_id', clientId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    return data;
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return null;
  }
}

export async function getAllProjects() {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('projects')
      .select('*, client:user_profiles(id, full_name, email)')
      .order('updated_at', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return data || [];
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return [];
  }
}

export async function updateProject(projectId: string, updates: Record<string, any>) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('projects')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', projectId)
      .select()
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    return data;
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return null;
  }
}

export async function createProject(payload: {
  clientId: string;
  projectName: string;
  description?: string;
}) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('projects')
      .insert({
        client_id: payload.clientId,
        project_name: payload.projectName,
        description: payload.description || '',
      })
      .select()
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    return data;
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return null;
  }
}

// ─── Deliverables ─────────────────────────────────────────────────────────────

export async function getDeliverables(projectId: string) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('deliverables')
      .select('*')
      .eq('project_id', projectId)
      .order('due_date', { ascending: true });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return data || [];
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return [];
  }
}

export async function createDeliverable(payload: {
  projectId: string;
  title: string;
  description?: string;
  phase?: string;
  dueDate?: string;
}) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('deliverables')
      .insert({
        project_id: payload.projectId,
        title: payload.title,
        description: payload.description || '',
        phase: payload.phase || 'General',
        due_date: payload.dueDate || null,
        status: 'upcoming',
      })
      .select()
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    return data;
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return null;
  }
}

export async function markDeliverableComplete(
  deliverableId: string,
  clientContactNumber: string,
  clientName: string,
  projectName: string,
  deliverableTitle: string,
  deliverableDescription: string
) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('deliverables')
      .update({
        status: 'completed',
        completed_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq('id', deliverableId)
      .select()
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    // Send WhatsApp notification
    await sendWhatsApp('deliverable_completed', clientContactNumber, {
      clientName,
      projectName,
      deliverableTitle,
      deliverableDescription,
      portalUrl: process.env.NEXT_PUBLIC_SITE_URL || 'https://renovoport1717.builtwithrocket.new',
    });
    return data;
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return null;
  }
}

// ─── Invoices ─────────────────────────────────────────────────────────────────

export async function getInvoices(clientId?: string) {
  const supabase = createClient();
  try {
    let query = supabase
      .from('invoices')
      .select('*, project:projects(project_name), client:user_profiles(full_name, email)')
      .order('created_at', { ascending: false });
    if (clientId) {
      query = query.eq('client_id', clientId);
    }
    const { data, error } = await query;
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return data || [];
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return [];
  }
}

export async function createInvoice(payload: {
  projectId: string;
  clientId: string;
  invoiceNumber: string;
  lineItems: any[];
  subtotal: number;
  tax: number;
  total: number;
  issueDate?: string;
  dueDate?: string;
  notes?: string;
}) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('invoices')
      .insert({
        project_id: payload.projectId,
        client_id: payload.clientId,
        invoice_number: payload.invoiceNumber,
        line_items: payload.lineItems,
        subtotal: payload.subtotal,
        tax: payload.tax,
        total: payload.total,
        issue_date: payload.issueDate || null,
        due_date: payload.dueDate || null,
        notes: payload.notes || null,
        status: 'pending',
      })
      .select('*, client:user_profiles(full_name, email, contact_number), project:projects(project_name)')
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    // Send WhatsApp notification
    if (data?.client) {
      await sendWhatsApp('invoice_sent', data.client.contact_number || '', {
        clientName: data.client.full_name,
        projectName: data.project?.project_name || '',
        invoiceNumber: payload.invoiceNumber,
        total: payload.total.toLocaleString(),
        dueDate: payload.dueDate || '',
        portalUrl: process.env.NEXT_PUBLIC_SITE_URL || 'https://renovoport1717.builtwithrocket.new',
      });
    }
    return data;
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return null;
  }
}

export async function markInvoicePaid(invoiceId: string) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('invoices')
      .update({
        status: 'paid',
        paid_date: new Date().toISOString().split('T')[0],
        updated_at: new Date().toISOString(),
      })
      .eq('id', invoiceId)
      .select('*, client:user_profiles(full_name, email, contact_number), project:projects(project_name)')
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    // Send WhatsApp notification
    if (data?.client) {
      await sendWhatsApp('invoice_paid', data.client.contact_number || '', {
        clientName: data.client.full_name,
        projectName: data.project?.project_name || '',
        invoiceNumber: data.invoice_number,
        total: Number(data.total).toLocaleString(),
        portalUrl: process.env.NEXT_PUBLIC_SITE_URL || 'https://renovoport1717.builtwithrocket.new',
      });
    }
    return data;
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return null;
  }
}

// ─── Messages ─────────────────────────────────────────────────────────────────

export async function getMessages(projectId: string) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('messages')
      .select('*, sender:user_profiles(full_name)')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return data || [];
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return [];
  }
}

export async function sendMessage(payload: {
  projectId: string;
  senderId: string;
  senderType: 'admin' | 'client';
  content: string;
  recipientContactNumber: string;
  recipientName: string;
  senderName: string;
  projectName: string;
}) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('messages')
      .insert({
        project_id: payload.projectId,
        sender_id: payload.senderId,
        sender_type: payload.senderType,
        content: payload.content,
        is_read: false,
      })
      .select()
      .single();
    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    // Send WhatsApp notification
    await sendWhatsApp('new_message', payload.recipientContactNumber, {
      recipientName: payload.recipientName,
      senderName: payload.senderName,
      projectName: payload.projectName,
      messagePreview: payload.content.length > 120 ? payload.content.slice(0, 120) + '...' : payload.content,
      portalUrl: process.env.NEXT_PUBLIC_SITE_URL || 'https://renovoport1717.builtwithrocket.new',
    });
    return data;
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return null;
  }
}

export async function markMessagesRead(projectId: string, senderType: 'admin' | 'client') {
  const supabase = createClient();
  try {
    await supabase
      .from('messages')
      .update({ is_read: true })
      .eq('project_id', projectId)
      .eq('sender_type', senderType)
      .eq('is_read', false);
  } catch {
    // non-critical
  }
}

// ─── Documents ────────────────────────────────────────────────────────────────

export async function getDocuments(projectId: string) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('documents')
      .select('*, uploader:user_profiles(full_name)')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return data || [];
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return [];
  }
}

export async function uploadDocument(
  projectId: string,
  uploadedBy: string,
  uploaderType: 'admin' | 'client',
  file: File,
  category: string = 'General'
) {
  const supabase = createClient();
  try {
    const ext = file.name.split('.').pop();
    const storagePath = `${projectId}/${uploadedBy}/${Date.now()}.${ext}`;

    const { error: storageError } = await supabase.storage
      .from('project-documents')
      .upload(storagePath, file, { upsert: false });

    if (storageError) {
      console.error('Storage upload error:', storageError.message);
      return null;
    }

    const sizeStr =
      file.size > 1024 * 1024
        ? `${(file.size / (1024 * 1024)).toFixed(1)} MB`
        : `${Math.round(file.size / 1024)} KB`;

    const { data, error } = await supabase
      .from('documents')
      .insert({
        project_id: projectId,
        uploaded_by: uploadedBy,
        uploader_type: uploaderType,
        file_name: file.name,
        file_type: ext || 'file',
        file_size: sizeStr,
        storage_path: storagePath,
        category,
      })
      .select()
      .single();

    if (error) {
      if (isSchemaError(error)) throw error;
      return null;
    }
    return data;
  } catch (err: any) {
    if (isSchemaError(err)) throw err;
    return null;
  }
}

export async function downloadDocument(storagePath: string) {
  const supabase = createClient();
  try {
    const { data, error } = await supabase.storage
      .from('project-documents')
      .createSignedUrl(storagePath, 3600);
    if (error) return null;
    return data.signedUrl;
  } catch {
    return null;
  }
}

// ─── Admin: Create Client ─────────────────────────────────────────────────────

export async function createClientAccount(payload: {
  email: string;
  fullName: string;
  projectName: string;
  tempPassword: string;
}) {
  const supabase = createClient();
  try {
    // Sign up the new client via admin auth
    const { data: authData, error: authError } = await supabase.auth.admin
      ? // If admin API available
        { data: null, error: new Error('Use server action for admin user creation') }
      : { data: null, error: new Error('Use server action for admin user creation') };

    if (authError) {
      console.error('Auth error:', authError.message);
      return null;
    }
    return authData;
  } catch (err: any) {
    console.error('Create client error:', err.message);
    return null;
  }
}
